import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ExternalLink, Award, AlertCircle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface ResultLink {
  id: string
  title: string
  url: string
  description: string | null
  display_order: number | null
}

export function CheckResultsSection() {
  const [resultLinks, setResultLinks] = useState<ResultLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchResultLinks()
  }, [])

  const fetchResultLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('result_links')
        .select('*')
        .eq('is_active', 1)
        .order('created_at', { ascending: false })

      if (error) throw error
      setResultLinks(data || [])
    } catch (err) {
      setError('Failed to load result links')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="py-8">

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Important Links</CardTitle>
          <CardDescription>
            Click on any link below to access results, jobs, and other important information.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-9 w-24" />
                </div>
              ))}
            </div>
          ) : resultLinks.length === 0 ? (
            <div className="text-center py-12">
              <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="font-inter text-muted-foreground">
                No result links available at the moment.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultLinks.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell className="font-medium">{link.title}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {link.description || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(link.url, '_blank')}
                            className="gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Link
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile View */}
              <div className="md:hidden space-y-3">
                {resultLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-medium text-foreground flex-1">
                        {link.title}
                      </h3>
                      <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}

